import { InlineBrandLoader } from "@/components/BrandLoader";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Settings,
  Bookmark,
  BookmarkCheck,
  Loader2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

interface Bookmark {
  time: number;
  label: string;
}

interface EnhancedVideoPlayerProps {
  url: string;
  poster?: string;
  onEnded?: () => void;
  savedBookmarks?: Bookmark[];
  onBookmarksChange?: (bookmarks: Bookmark[]) => void;
  autoplay?: boolean;
}

export function EnhancedVideoPlayer({ 
  url, 
  poster,
  onEnded,
  savedBookmarks = [],
  onBookmarksChange,
  autoplay = false
}: EnhancedVideoPlayerProps) {
  const { toast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showCaptions, setShowCaptions] = useState(false);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(savedBookmarks);
  const [hasStarted, setHasStarted] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Detect iOS and mobile
  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsIOS(iOS);
    setIsMobile(mobile);
    
    // For iOS, we need to explicitly load the video after the component mounts
    if (iOS && videoRef.current) {
      // Reset and load for iOS
      const video = videoRef.current;
      video.load();
    }
  }, [url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);
    const handleVideoEnded = () => {
      onEnded?.();
      setIsPlaying(false);
    };
    
    // Sync state with actual video events for reliability
    const handlePlay = () => {
      setIsPlaying(true);
      setHasStarted(true);
    };
    const handlePause = () => setIsPlaying(false);

    const handleWaiting = () => setIsBuffering(true);
    const handleCanPlay = () => { setIsBuffering(false); setHasError(false); };
    const handlePlaying = () => { setIsBuffering(false); setHasError(false); };
    const handleCanPlayThrough = () => setIsBuffering(false);
    
    // Handle video errors
    const handleError = (e: Event) => {
      const video = e.target as HTMLVideoElement;
      const error = video?.error;
      console.error('Video error:', error);
      setIsBuffering(false);
      setHasError(true);
      
      if (error) {
        switch (error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            setErrorMessage('Video playback was aborted');
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            setErrorMessage('Network error while loading video');
            break;
          case MediaError.MEDIA_ERR_DECODE:
            setErrorMessage('Video format not supported');
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            setErrorMessage('Video source not supported');
            break;
          default:
            setErrorMessage('An error occurred loading the video');
        }
      }
    };
    
    // Handle stalled/suspended - common on mobile
    const handleStalled = () => {
      console.log('Video stalled - attempting to recover');
      // Don't show buffering immediately on stall, give it time
    };
    
    const handleSuspend = () => {
      // Video loading was suspended (normal on mobile to save bandwidth)
      console.log('Video loading suspended');
    };

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('ended', handleVideoEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('error', handleError);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('suspend', handleSuspend);

    // Autoplay if enabled
    if (autoplay) {
      video.play().catch(() => {
        // Autoplay was prevented, user will need to click play
      });
    }

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('ended', handleVideoEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('error', handleError);
      video.removeEventListener('stalled', handleStalled);
      video.removeEventListener('suspend', handleSuspend);
    };
  }, [onEnded, autoplay]);

  const togglePlayPause = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (video.paused) {
        await video.play();
      } else {
        video.pause();
      }
    } catch (error) {
      console.error('Video playback error:', error);
    }
  };

  const handleTapToPlay = async () => {
    const video = videoRef.current;
    if (!video) return;
    
    try {
      setHasError(false);
      // On mobile, load the video first then play
      if (video.readyState < 2) {
        video.load();
      }
      await video.play();
    } catch (error) {
      console.error('Video playback error:', error);
      setHasError(true);
      setErrorMessage('Unable to play video. Please try again.');
    }
  };

  const handleRetry = () => {
    const video = videoRef.current;
    if (!video) return;
    
    setHasError(false);
    setErrorMessage('');
    setIsBuffering(true);
    
    // Force reload the video
    const currentSrc = video.src;
    video.src = '';
    video.load();
    video.src = currentSrc;
    video.load();
  };

  const handleVolumeChange = (value: number[]) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = value[0];
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (value: number[]) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = value[0];
    setCurrentTime(value[0]);
  };

  const changePlaybackRate = (rate: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.playbackRate = rate;
    setPlaybackRate(rate);
    toast({
      title: "Playback speed changed",
      description: `Playing at ${rate}x speed`,
    });
  };

  const toggleCaptions = () => {
    const video = videoRef.current;
    if (!video) return;

    const tracks = video.textTracks;
    if (tracks.length > 0) {
      tracks[0].mode = showCaptions ? 'hidden' : 'showing';
      setShowCaptions(!showCaptions);
    }
  };

  const addBookmark = () => {
    const video = videoRef.current;
    if (!video) return;

    const time = video.currentTime;
    const label = `Bookmark at ${formatTime(time)}`;
    const newBookmarks = [...bookmarks, { time, label }];
    setBookmarks(newBookmarks);
    onBookmarksChange?.(newBookmarks);
    
    toast({
      title: "Bookmark added",
      description: label,
    });
  };

  const jumpToBookmark = (time: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = time;
    setCurrentTime(time);
  };

  const toggleFullscreen = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if ((video as any).webkitEnterFullscreen) {
        // iOS Safari fallback
        (video as any).webkitEnterFullscreen();
      } else if ((video as any).webkitRequestFullscreen) {
        // Older WebKit browsers
        (video as any).webkitRequestFullscreen();
      } else if (video.requestFullscreen) {
        // Standard API (works on Android Chrome and modern browsers)
        await video.requestFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      <div ref={containerRef} className="relative aspect-video bg-black rounded-lg overflow-hidden group">
        {/* Video element - iOS uses completely native approach */}
        {isIOS ? (
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            playsInline
            controls
            preload="none"
            poster={poster}
            onPlay={() => { setIsPlaying(true); setHasStarted(true); setHasError(false); }}
            onPause={() => setIsPlaying(false)}
            onLoadedData={() => {
              if (videoRef.current) {
                setDuration(videoRef.current.duration);
                setIsBuffering(false);
              }
            }}
            onError={() => {
              setHasError(true);
              setErrorMessage('Unable to load video on this device');
            }}
          >
            <source src={url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <video
            key={`${isMobile ? 'mobile' : 'desktop'}`}
            ref={videoRef}
            src={url}
            poster={poster}
            className="w-full h-full object-contain"
            playsInline={true}
            controls={isMobile}
            preload={isMobile ? "metadata" : "auto"}
            onPlay={() => { setIsPlaying(true); setHasStarted(true); setHasError(false); }}
            onPause={() => setIsPlaying(false)}
            onLoadedData={() => {
              if (videoRef.current) {
                setDuration(videoRef.current.duration);
                setIsBuffering(false);
              }
            }}
            onLoadStart={() => {
              if (!hasStarted) setIsBuffering(true);
            }}
          >
            <track kind="captions" />
          </video>
        )}

        {/* Error overlay with retry button */}
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-40">
            <div className="text-center p-4">
              <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-4">
                <Play className="h-8 w-8 text-destructive" />
              </div>
              <p className="text-white text-sm mb-4">{errorMessage || 'Unable to load video'}</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Buffering spinner overlay */}
        {isBuffering && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-30 pointer-events-none"><InlineBrandLoader /></div>
        )}

        {/* Tap-to-play overlay for non-mobile or desktop - on mobile we use native controls */}
        {!hasStarted && !isMobile && (
          <div 
            className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer z-20"
            onClick={handleTapToPlay}
          >
            <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center shadow-lg animate-pulse">
              <Play className="h-10 w-10 text-primary-foreground ml-1" />
            </div>
          </div>
        )}

        {/* Custom Controls Overlay - only on desktop, mobile uses native controls */}
        {!isMobile && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
          {/* Progress Bar */}
          <div className="mb-4">
            <input
              type="range"
              min={0}
              max={duration || 100}
              step={0.1}
              value={currentTime}
              onChange={(e) => handleSeek([parseFloat(e.target.value)])}
              className="w-full h-2 bg-white/30 rounded-lg appearance-none cursor-pointer accent-primary"
              style={{
                background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${(currentTime / (duration || 1)) * 100}%, rgba(255,255,255,0.3) ${(currentTime / (duration || 1)) * 100}%, rgba(255,255,255,0.3) 100%)`
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onTouchEnd={(e) => { e.preventDefault(); togglePlayPause(); }}
                onClick={togglePlayPause}
                className="h-10 w-10 flex items-center justify-center text-white hover:bg-white/20 rounded-md transition-all active:scale-95"
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
              </button>

              <button
                type="button"
                onTouchEnd={(e) => { e.preventDefault(); toggleMute(); }}
                onClick={toggleMute}
                className="h-9 w-9 flex items-center justify-center text-white hover:bg-white/20 rounded-md transition-all active:scale-95"
              >
                {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>

              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolumeChange([parseFloat(e.target.value)])}
                className="w-16 h-1.5 bg-white/30 rounded-lg appearance-none cursor-pointer accent-white"
              />

              <span className="text-xs font-mono text-white/90 ml-1 whitespace-nowrap">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onTouchEnd={(e) => { e.preventDefault(); addBookmark(); }}
                onClick={addBookmark}
                className="h-9 w-9 flex items-center justify-center text-white hover:bg-white/20 rounded-md transition-all active:scale-95"
                title="Add bookmark"
              >
                <Bookmark className="h-4 w-4" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="h-9 w-9 flex items-center justify-center text-white hover:bg-white/20 rounded-md transition-all active:scale-95"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-background/95 backdrop-blur-sm">
                  <DropdownMenuItem onClick={() => changePlaybackRate(0.5)} className="cursor-pointer">
                    0.5x Speed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changePlaybackRate(0.75)} className="cursor-pointer">
                    0.75x Speed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changePlaybackRate(1)} className="cursor-pointer">
                    Normal Speed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changePlaybackRate(1.25)} className="cursor-pointer">
                    1.25x Speed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changePlaybackRate(1.5)} className="cursor-pointer">
                    1.5x Speed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changePlaybackRate(2)} className="cursor-pointer">
                    2x Speed
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                type="button"
                onTouchEnd={(e) => { e.preventDefault(); toggleFullscreen(); }}
                onClick={toggleFullscreen}
                className="h-9 w-9 flex items-center justify-center text-white hover:bg-white/20 rounded-md transition-all active:scale-95"
              >
                <Maximize className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Bookmarks */}
      {bookmarks.length > 0 && (
        <div className="space-y-2 p-4 bg-gradient-to-r from-muted/50 to-muted/30 rounded-lg border border-border/50">
          <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground">
            <BookmarkCheck className="h-4 w-4 text-primary" />
            Bookmarks
          </h4>
          <div className="flex flex-wrap gap-2">
            {bookmarks.map((bookmark, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-all hover:scale-105 shadow-sm"
                onClick={() => jumpToBookmark(bookmark.time)}
              >
                {bookmark.label}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Current Speed Indicator */}
      {playbackRate !== 1 && (
        <div className="flex items-center justify-center">
          <Badge variant="outline" className="text-xs font-mono">
            Playing at {playbackRate}x speed
          </Badge>
        </div>
      )}
    </div>
  );
}
